<template>
  <section>
    <scroll-container
      class="pt-2 mt-n30px"
      :height-offset="isInEditMode ? '55px' : '125px'"
    >
      <b-row v-if="loading">
        <b-col
          sm="12"
          class="text-center"
        >
          <b-skeleton
            class="d-inline-block"
            animation="wave"
            width="40%"
            height="900px"
          />
        </b-col>
      </b-row>
      <b-container
        v-else
        class="container-small"
      >
        <b-card
          class="mb-3"
        >
          <div class="mb-2">
            <b-card-title
              v-if="model.videoAdModel.id === 0"
              class="mb-0"
            >
              {{ $tk('ClientPortal.Form.NewVideoAd') }}
            </b-card-title>
            <b-card-title
              v-else
              class="mb-0"
            >
              {{ $tk('ClientPortal.Form.EditVideoAd') }}
            </b-card-title>
            <b-breadcrumb class="m-0 p-0">
              <b-breadcrumb-item :to="`/${$route.params.accountId}/campaign/edit/${model.campaignModel.id}`">
                {{ model.campaignModel.name }}
              </b-breadcrumb-item>
              <b-breadcrumb-item :to="`/${$route.params.accountId}/adgroup/edit?adGroupId=${model.adGroup.id}&campaignId=${model.campaignModel.id}`">
                {{ model.adGroup.name }}
              </b-breadcrumb-item>
              <b-breadcrumb-item
                v-if="model.videoAdModel.id === 0"
                active
              >
                {{ $tk('ClientPortal.Form.NewAd') }}
              </b-breadcrumb-item>
              <b-breadcrumb-item
                v-else
                active
              >
                {{ model.videoAdModel.name }}
              </b-breadcrumb-item>
            </b-breadcrumb>
            <hr class="m-0 mt-1">
          </div>
          <b-form-group
            id="Name"
            class="mb-3"
            :label="$tk('ClientPortal.Form.AdName') + ' *'"
            :invalid-feedback="errors['Name']"
            :state="errors['Name'] == null ? null : false"
          >
            <AdNameField
              v-model="model.videoAdModel.name"
              :variables="adNameVariables"
              :placeholder="$tk('ClientPortal.Form.AdNamePlaceholder')"
              :state="errors['Name'] == null ? null : false"
            />
          </b-form-group>
          <b-card-title class="mb-25">
            {{ $tk('ClientPortal.Form.Info') }}
          </b-card-title>
          <b-card-text class="mb-2">
            {{ $tk('ClientPortal.Form.Info.Description') }}
          </b-card-text>
          <b-form-group
            class="mb-2"
          >
            <legend class="col-form-label pt-0">
              {{ $tk('ClientPortal.Form.TopOverLayText') }}
              <GjIcon
                v-b-tooltip.hover="$tk('ClientPortal.Form.TopOverlayTextTooltip')"
                name="Info"
              />
            </legend>
            <b-form-input
              v-model="model.videoAdModel.title"
              type="text"
              :placeholder="$tk('ClientPortal.Form.TopOverLayTextPlaceholder')"
              :maxlength="30"
            />
            <span class="col-sm-5 float-right text-right">
              <span>{{ model.videoAdModel.title.length }}</span>
              / <span>30</span>
            </span>
          </b-form-group>
          <b-form-group
            id="Url"
            class="mb-2"
            :invalid-feedback="errors['Url']"
            :state="errors['Url'] == null ? null : false"
          >
            <legend class="col-form-label pt-0">
              {{ $tk('ClientPortal.Form.Url') }} *
              <GjIcon
                v-b-tooltip.hover="$tk('ClientPortal.Form.URLTooltip')"
                name="Info"
              />
            </legend>
            <b-form-input
              v-model="model.videoAdModel.url"
              type="text"
              :placeholder="$tk('ClientPortal.Form.UrlPlaceholder')"
              :state="errors['Url'] == null ? null : false"
            />
          </b-form-group>
          <b-button
            v-b-toggle="'url-builder'"
            class="bg-white mb-2"
            size="sm"
            variant="outline-primary"
          >
            <GjIcon
              class="mr-50"
              name="Hyperlink"
            />
            <span class="when-open">
              {{ $tk('ClientFormat.Form.CloseUrlBuilder') }}
              <GjIcon
                class="mr-50"
                name="ArrowUp"
              />
            </span>
            <span class="when-closed">
              {{ $tk('ClientFormat.Form.OpenUrlBuilder') }}
              <GjIcon
                class="mr-50"
                name="ArrowDown"
              />
            </span>
          </b-button>
          <b-collapse
            :id="'url-builder'"
          >
            <url-builder
              :ref="'utm-'+model.videoAdModel.id"
              :key="model.videoAdModel.id"
              v-model="model.videoAdModel.url"
              :options="model.utmOptions"
            />
          </b-collapse>
          <b-form-group
            class="mb-2"
          >
            <legend class="col-form-label pt-0">
              {{ $tk('ClientPortal.Form.ImpressionTag') }}
              <GjIcon
                v-b-tooltip.hover="$tk('ClientPortal.Form.ImpressionTagTooltip')"
                name="Info"
              />
            </legend>
            <b-form-textarea
              v-model="model.videoAdModel.impressionTag"
              type="text"
              :placeholder="$tk('ClientPortal.Form.ImpressionTagPlaceholder')"
              rows="3"
            />
          </b-form-group>
          <b-form-group
            v-if="model.videoAdModel.skippable"
            class="mb-2"
          >
            <legend class="col-form-label pt-0">
              {{ $tk('ClientPortal.Form.SkipTag') }}
              <GjIcon
                v-b-tooltip.hover="$tk('ClientPortal.Form.SkipTagTooltip')"
                name="Info"
              />
            </legend>
            <b-form-textarea
              v-model="model.videoAdModel.skipTag"
              type="text"
              :placeholder="$tk('ClientPortal.Form.SkipTagPlaceholder')"
              rows="3"
            />
          </b-form-group>
          <b-form-group
            class="mb-2"
          >
            <legend class="col-form-label pt-0">
              {{ $tk('ClientPortal.Form.ActionTag') }}
              <GjIcon
                v-b-tooltip.hover="$tk('ClientPortal.Form.ActionTagTooltip')"
                name="Info"
              />
            </legend>
            <b-form-textarea
              v-model="model.videoAdModel.actionTag"
              type="text"
              :placeholder="$tk('ClientPortal.Form.ActionTagPlaceholder')"
              rows="3"
            />
          </b-form-group>
          <b-row>
            <b-col sm="8">
              <b-card-text class="mb-50">
                {{ $tk('ClientPortal.Form.EnableExternalAds') }}
                <GjIcon
                  v-b-tooltip.hover="$tk('ClientPortal.Form.EnableExternalAdsTooltip')"
                  name="Info"
                />
              </b-card-text>
            </b-col>
            <b-col
              sm="4"
              class="text-right"
            >
              <b-form-checkbox
                v-model="model.videoAdModel.external"
                class="d-block"
                switch
                @change="externalSwitched()"
              />
            </b-col>
          </b-row>
          <hr class="mt-50 mb-1">
          <div v-show="!model.videoAdModel.external">
            <b-form-group
              class="mb-2"
            >
              <legend class="col-form-label pt-0">
                {{ $tk('ClientPortal.Form.Video') + ' *' }}
                <GjIcon
                  v-b-tooltip.hover="$tk('ClientPortal.Form.VideoTooltip')"
                  name="Info"
                />
              </legend>
              <multiselect
                v-model="selectedVideo"
                :options="model.videoAdModel.videos"
                :searchable="true"
                :show-labels="false"
                :placeholder="$tk('ClientPortal.Form.SearchOrSelectVideo')"
                :option-height="50"
                track-by="id"
                label="title"
                @select="(selectedVideo, id) => setSelectedVideo(selectedVideo)"
              >
                <template
                  slot="option"
                  slot-scope="props"
                >
                  <b-row>
                    <b-col sm="2">
                      <div
                        v-if="props.option.thumbnailUrl != null"
                        class="selected-item-thumbnail d-inline-block"
                        :style="{ backgroundImage: 'url(' + props.option.thumbnailUrl + ')' }"
                      />
                    </b-col>
                    <b-col
                      class=" d-flex"
                      sm="10"
                    >
                      <div class="selected-item-title d-inline-block align-self-center">
                        {{ props.option.title }}
                      </div>
                    </b-col>
                  </b-row>
                </template>
                <template
                  slot="caret"
                  slot-scope="{ toggle }"
                >
                  <div
                    class="multiselect__select"
                    @mousedown.prevent.stop="toggle()"
                  >
                    <GjIcon
                      class="icon-wrapper"
                      name="ArrowDown_fill"
                      size="21"
                    />
                  </div>
                </template>
              </multiselect>
            </b-form-group>
            <text-divider :text="$tk('ClientPortal.Form.Or')" />
            <div class="text-center mt-2">
              <b-button
                variant="outline-primary"
                size="sm"
                @click.prevent="uploadVideo"
              >
                <GjIcon
                  name="Plus"
                  class="mr-50"
                />
                {{ $tk('ClientPortal.Form.UploadAVideo') }}
              </b-button>
            </div>
            <span
              v-if="errors['VideoId']"
              class="field-validation-valid text-danger small"
            >
              {{ errors['VideoId'] }}
            </span>
          </div>
          <div v-show="model.videoAdModel.external">
            <b-form-group
              id="External"
              class="mb-2"
              :invalid-feedback="errors['External']"
              :state="errors['External'] == null ? null : false"
            >
              <legend class="col-form-label pt-0">
                {{ $tk('ClientPortal.Form.VastUrl') }}
                <GjIcon
                  v-b-tooltip.hover="$tk('ClientPortal.Form.VastUrlTooltip')"
                  name="Info"
                />
              </legend>
              <b-form-input
                v-model="model.videoAdModel.vastUrl"
                type="text"
                :placeholder="$tk('ClientPortal.Form.SearchOrSelectVideo')"
                :state="errors['External'] == null ? null : false"
              />
            </b-form-group>
            <b-form-group
              v-if="platformAccessManager.canSeeYoutubeVideoId() && configuration.clientConfiguration.showYoutubeVideoId"
              class="mb-0"
              :invalid-feedback="errors['External']"
              :state="errors['External'] == null ? null : false"
            >
              <legend class="col-form-label pt-0">
                {{ $tk('ClientPortal.Form.YoutubeVideoId') }}
                <GjIcon
                  v-b-tooltip.hover="$tk('ClientPortal.Form.YoutubeVideoIdTooltip')"
                  name="Info"
                />
              </legend>
              <b-form-input
                v-model="model.videoAdModel.youtubeVideoId"
                type="text"
                :placeholder="$tk('ClientPortal.Form.SearchOrSelectVideo')"
                :state="errors['External'] == null ? null : false"
              />
            </b-form-group>
          </div>
        </b-card>
        <b-card
          class="mb-3"
          no-body
        >
          <b-card-title
            v-b-toggle.collapse-ad-preview
            class="d-flex justify-content-between p-2 mb-0"
            @click="showPreview(selectedVideo)"
          >
            <div
              class="text-primary"
            >
              <GjIcon name="Show" />
              {{ $tk('ClientPortal.Form.AdCreation.AdPreview') }}
            </div>
            <span class="when-open">
              <GjIcon name="ArrowUp" />
            </span>
            <span class="when-closed">
              <GjIcon name="ArrowDown" />
            </span>
          </b-card-title>
          <b-collapse
            id="collapse-ad-preview"
          >
            <b-card-body class="p-0">
              <hr class="mt-0 mb-0">
              <div
                v-if="previewVideo"
                class="card w-100"
              >
                <div class="card-body bg-white">
                  <div
                    v-if="encoding"
                    class="encoding-text"
                  >
                    <GjIcon
                      name="SettingsAlt"
                      size="18"
                    />
                    {{ $tk('ClientPortal.Form.AdCreation.VideoIsBeingEncodedPleaseWait') }}
                  </div>
                  <div v-if="previewVideo.mediaUrl">
                    <div class="video-ad">
                      <video
                        id="video"
                        ref="videoAdPreview"
                        width="100%"
                        controls
                      />
                    </div>
                  </div>
                </div>
              </div>
              <span
                v-else
                class="encoding-text"
              >
                {{ $tk('ClientPortal.Form.AdCreation.NoVideoHasBeenSelectedYet') }}
              </span>
            </b-card-body>
          </b-collapse>
        </b-card>
      </b-container>
    </scroll-container>
    <div class="border-top text-right py-1">
      <div class="container-small mx-auto">
        <b-button
          v-if="isInEditMode"
          class="bg-white"
          variant="outline-dark"
          size="sm"
          :disabled="saving"
          @click="closeEditModal"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          v-else
          :to="`/${$route.params.accountId}`"
          class="bg-white"
          variant="outline-dark"
          size="sm"
          :disabled="saving"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          class="bg-white ml-50"
          variant="primary"
          size="sm"
          :disabled="saving || !isFormComplete"
          @click="saveVideoAd()"
        >
          {{ isInEditMode ? $tk('ClientPortal.Form.Actions.Save') : $tk('ClientPortal.Form.Actions.CreateAd') }}
        </b-button>
      </div>
    </div>
    <b-modal
      ref="upload-video-modal"
      variant="primary"
      :no-close-on-esc="isUploading && barVariant !== 'danger'"
      :no-close-on-backdrop="isUploading && barVariant !== 'danger'"
      :hide-header-close="isUploading && barVariant !== 'danger'"
      centered
      @hidden="clearErrorMsg"
    >
      <template v-slot:modal-title>
        <h5 class="modal-title">
          <span>
            {{ $tk('ClientPortal.Form.UploadVideo') }}
          </span>
        </h5>
      </template>
      <b-row>
        <b-col sm="12">
          <input
            id="video-file-name"
            v-model="videoEdit.fileName"
            type="text"
            :placeholder="$tk('ClientPortal.Form.VideoFileNamePlaceHolder')"
            class="form-control form-control-sm mb-2"
          >
          <input
            id="video-file-id"
            v-model="videoEdit.videoId"
            type="hidden"
            class="form-control mb-2"
          >
          <input
            id="upload-account-id"
            type="hidden"
            :value="$route.params.accountId"
          >
          <vue-dropzone
            id="dropzone"
            ref="videoDropzone"
            :options="dropzoneOptions"
            :use-custom-slot="true"
            style="width:100%; text-align: center;"
            @vdropzone-file-added="passFile"
          >
            <div class="dropzone-custom-content">
              <b-button
                variant="outline-primary"
                size="sm"
                @click.stop="openVideoUpload"
              >
                <GjIcon
                  class="mr-50"
                  name="Upload"
                />
                {{ $t('ClientPortal.Form.UploadVideo') }}
              </b-button>
              <div
                width="100%"
                style="pointer-events:none;"
              >
                {{ $tk('ClientPortal.Form.Max8MBFileAllowed') }}
              </div>
            </div>
          </vue-dropzone>
          <input
            id="videoFile"
            ref="openUpload"
            hidden
            accept="video/*"
            type="file"
            name="video"
            :disabled="isUploading && barVariant !== 'danger'"
            @input="onFileSelected"
          >
          <div
            v-if="isUploading"
            class="d-flex flex-column w-100 col-12 p-0 pt-1"
          >
            <b-col
              md="12"
              class=" px-0"
            >
              <uploader-bar
                v-model="fileProgress"
                :variant="barVariant"
                :message="uploaderBarMessage"
              />
            </b-col>
          </div>

        </b-col>
      </b-row>
      <b-row>
        <b-col sm="12">
          <div
            id="progress-bar-holder"
            ref="progressBarHolder"
          />
        </b-col>
      </b-row>
      <template v-slot:modal-footer>
        <b-button
          variant="primary"
          size="sm"
          :disabled="!videoEdit.videoId || barVariant === 'danger'"
          @click="updateVideoName(videoEdit)"
        >
          {{ $tk('ClientPortal.Form.Actions.Save') }}
        </b-button>
      </template>

    </b-modal>
  </section>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import Hls from 'hls.js'
import notification from '@/mixins/toastification'
import sweetAlert from '@/mixins/sweetAlert'
import Multiselect from 'vue-multiselect'
import ScrollContainer from '@/components/ScrollContainer.vue'
import UrlBuilder from '@/components/UrlBuilder.vue'
import TextDivider from '@/components/TextDivider.vue'
import { PlatformAccessManager } from '@/utils/accessManager/accessManager'
import { mapGetters } from 'vuex'
import axiosIns from '@/libs/axios'
import axios from 'axios'
import UploaderBar from '@/components/UploaderBar.vue'
import AdsService from '@/api/client/ads.service'
import VideoService from '@/api/client/video.service'
import scrollToError from '@/mixins/scrollToError'
import vueDropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import AdNameField from '@/components/AdNameField.vue'
import { AD_NAME_VARIABLES, findUnknownTokens } from '@/utils/adNameTokens'

export default {
  inject: ['closeEditModal'],
  components: {
    GjIcon,
    Multiselect,
    ScrollContainer,
    UrlBuilder,
    TextDivider,
    UploaderBar,
    vueDropzone,
    AdNameField,
  },
  mixins: [notification, sweetAlert],
  props: {
    // eslint-disable-next-line vue/require-default-prop
    adId: Number,
    // eslint-disable-next-line vue/require-default-prop
    adGroupId: Number,
    // eslint-disable-next-line vue/require-default-prop
    catalogSetId: {
      type: String,
    },
    saving: {
      type: Boolean,
    },
  },
  data() {
    return {
      model: {},
      videoEdit: {},
      selectedVideo: [],
      preview: false,
      previewVideo: null,
      errors: {},
      value: null,
      loading: false,
      platformAccessManager: PlatformAccessManager,
      isUploading: false,
      fileProgress: 0,
      barVariant: 'primary',
      uploaderBarMessage: 'null',
      isFileUploading: false,
      dropzoneOptions: {
        maxFiles: 1,
        parallelUploads: 1,
        uploadMultiple: false,
        acceptedFiles: '.mp4,.flv',
        url: 'url',
        thumbnailWidth: null,
        timeout: 0,
        thumbnailHeight: null,
        addRemoveLinks: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      },
      encoding: false,
    }
  },
  computed: {
    ...mapGetters(['configuration']),
    isFormComplete() {
      const {
        name, url, external, vastUrl, youtubeVideoId, videoId,
      } = this.model.videoAdModel

      return name && url && (external ? (vastUrl || youtubeVideoId) : videoId)
    },
    isInEditMode() {
      return this.adId > 0
    },
    adNameVariables() {
      return AD_NAME_VARIABLES.filter(key => key !== 'format')
    },
  },
  async mounted() {
    this.loading = true
    try {
      this.model = await AdsService.getAd(this.adId, this.adGroupId, this.catalogSetId, this.$route.params.accountId)
    } catch (error) {
      this.error(this.$tk('ClientPortal.Form.FailedToLoadData'))
    } finally {
      if (this.model.videoAdModel.videoId > 0) {
      // eslint-disable-next-line no-plusplus
        for (let i = 0; i < this.model.videoAdModel.videos.length; i++) {
          const video = this.model.videoAdModel.videos[i]
          if (video.id === this.model.videoAdModel.videoId) {
            this.selectedVideo = video
            // eslint-disable-next-line no-unsafe-finally
            // return
          }
        }
      }
      this.loading = false
    }
  },
  methods: {
    setSaving(value) {
      this.$emit('onUpdate', value)
    },
    externalSwitched() {
      this.model.videoAdModel.vastUrl = ''
      this.model.videoAdModel.videoId = 0
    },
    validateAdName() {
      const ad = this.model.videoAdModel
      this.$set(this.errors, 'Name', null)
      const unknown = findUnknownTokens(ad.name)
      if (unknown.length > 0) {
        const tokens = unknown.map(k => `{{${k}}}`).join(', ')
        this.$set(this.errors, 'Name', `Unknown variables: ${tokens}`)
        return false
      }
      return true
    },
    async saveVideoAd() {
      this.setSaving(true)
      if (!this.validateAdName()) {
        this.$nextTick(() => scrollToError(this.errors))
        this.setSaving(false)
        return
      }
      try {
        const response = await AdsService.createVideoAd(this.model.videoAdModel, this.$route.params.accountId)
        if (response.success) {
          const resolvedName = response.name || response.adName || (response.model && response.model.name) || this.model.videoAdModel.name
          this.model.videoAdModel.name = resolvedName
          if (this.isInEditMode) {
            const { entity } = this.$parent.$parent.$parent
            entity.name = resolvedName
            this.closeEditModal()
          } else {
            this.model.videoAdModel = {}
            this.sweetAlertAfterAdCreation()
          }
        } else {
          this.errors = response.errors
          this.error(this.$tk('ClientPortal.Ads.ErrorSavingChanges'))
          this.$nextTick(() => {
            scrollToError(this.errors)
          })
          this.setSaving(false)
        }
      } catch (error) {
        this.error(error.message)
        this.setSaving(false)
      }
    },
    setSelectedVideo(selectedVideo) {
      this.model.videoAdModel.videoId = selectedVideo.id
    },
    uploadVideo() {
      this.$refs['upload-video-modal'].show()
    },
    async updateVideoName(videoEdit) {
      try {
        const response = await VideoService.updateVideo(videoEdit.videoId, videoEdit.fileName, this.$route.params.accountId)
        if (response.success) {
          this.$refs['upload-video-modal'].hide()
          const exists = this.model.videoAdModel.videos.filter(x => x.id === videoEdit.videoId)
          const data = {
            id: videoEdit.videoId,
            title: videoEdit.fileName,
            thumbnailUrl: null,
          }
          if (exists.length === 0) {
            this.model.videoAdModel.videos.push(data)
            this.videoEdit.id = 0
            this.videoEdit.fileName = ''
          }
          this.selectedVideo = data
          this.success(response.message)
        } else {
          this.error(response.message)
        }
      } catch (error) {
        this.error(error.message)
      }
    },
    async showPreview(selectedVideo) {
      if (this.preview) {
        this.$refs.videoAdPreview.pause()
        this.preview = false
      } else {
        this.encoding ? this.previewVideo = {} : this.previewVideo = null
        if (selectedVideo != null && selectedVideo.id > 0) {
          try {
            const response = await VideoService.getVideo(selectedVideo.id, this.$route.params.accountId)
            this.previewVideo = response
            if (this.previewVideo.mediaUrl != null) {
              this.preview = true
              this.$nextTick(() => {
                this.loadVideo(this.previewVideo.mediaUrl)
                this.encoding = false
              })
            } else if (!this.encoding) {
              this.encoding = true
              this.error(this.previewVideo.message)
            }
          } catch (error) {
            this.error(error.message)
          }
        }
      }
    },
    clearErrorMsg() {
      if (this.barVariant === 'danger' && this.isUploading) {
        this.isUploading = false
        this.barVariant = 'primary'
      }
    },
    checkFileType(type) {
      return type.startsWith('video')
    },
    passFile(file) {
      this.$refs.videoDropzone.removeFile(file)
      this.onFileSelected(file, true)
    },
    async onFileSelected(e, dragAndDrop = false) {
      /* eslint-disable no-else-return, no-useless-return */
      if (this.isFileUploading) {
        return
      } else {
        this.isFileUploading = true
        const $this = this
        this.isUploading = true
        this.fileProgress = 0
        this.barVariant = 'primary'
        let video
        if (dragAndDrop) {
          video = e
          if (!this.checkFileType(e.type)) {
            this.isFileUploading = false
            this.barVariant = 'danger'
            this.uploaderBarMessage = this.$tk('ClientPortal.Ads.VideoAd.UploadFailedWrongFileExtension')
            return
          }
        } else { /* eslint-disable-next-line */
          video = e.target.files[0]
          if (!this.checkFileType(e.target.files[0].type)) {
            this.isFileUploading = false
            this.barVariant = 'danger'
            this.uploaderBarMessage = this.$tk('ClientPortal.Ads.VideoAd.UploadFailedWrongFileExtension')
            return
          }
        }
        const params = {
          fileName: video.name,
          mimeType: video.type,
        }
        const headers = {
          'Access-Control-Allow-Origin': '*',
        }
        await axiosIns.post(`upload/presignedurl?accountId=${this.$route.params.accountId}`, params, {
          headers,
        }).then(response => {
          const signedUrl = response.data.result.url
          const options = {
            headers: {
              'Access-Control-Allow-Origin': '*',
              'Content-Type': 'application/octet-stream',
              'Passed-Req-Key': response.data.result.requestKey,

            },
            onUploadProgress: progressEvent => {
              const { total } = progressEvent
              const totalLength = progressEvent.lengthComputable ? total : null
              if (totalLength !== null) {
                this.fileProgress = Math.round((progressEvent.loaded * 100) / total)
              }
            },
          }

          if (response.data.result.isCeph === true) {
            options.headers['x-amz-acl'] = 'public-read'
          }

          return axios.put(signedUrl, video, options)
            .then(async result => {
              const encodeHeader = {
                downloadUrl: result.config.headers['Passed-Req-Key'],
              }
              await axiosIns.post('upload/presignedurl/encode', encodeHeader)
                .then(res => {
                  const selectedVideo = {
                    id: res.data.result.id,
                    title: res.data.result.name,
                  }
                  $this.videoEdit.fileName = res.data.result.name
                  $this.videoEdit.videoId = res.data.result.id
                  $this.selectedVideo = selectedVideo
                  $this.setSelectedVideo(selectedVideo)
                  this.isUploading = false
                  this.fileProgress = 0
                })
            })
            .catch(err => {
              if (err) {
                this.isUploading = true
                this.fileProgress = 100
                this.barVariant = 'danger'
              }
            })
        })
          .catch(err => {
            if (err) {
              this.isUploading = true
              this.fileProgress = 100
              this.barVariant = 'danger'
            }
          })
        this.isFileUploading = false
      }
    },
    loadVideo(url) {
      if (Hls.isSupported()) {
        const video = document.getElementById('video')
        if (url.endsWith('.mp4')) {
          video.src = url
          video.play()
        } else {
          const hls = new Hls()
          hls.loadSource(url)
          hls.attachMedia(video)
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            video.play()
          })
        }
      }
    },
    openVideoUpload() {
      this.$refs.openUpload.click()
    },
  },
}
</script>
<style lang="scss">
#upload-account-id + label {
  border: 2px dashed #e5e5e5 !important;
  padding: 50px 20px;
  border-radius: 0.25rem;
  cursor: pointer;
  &:hover {
    background-color: #f6f6f6;;
  }
}
.multiselect__content{
  max-width: 100% !important;
}
</style>
<style lang="scss" >
@import '@/assets/scss/libs/_vue-multiselect.scss';
</style>
